import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { ActionEnum } from "notifications/values/action/action";
import NotificationMessage from "notifications/values/notification-message/notification-message";
import NotificationRecipient from "notifications/values/notification-recipient/notification-recipient";
import NotificationRecipientSchema from "notifications/values/notification-recipient/notification-recipient-schema";
import NotificationTopic from "notifications/values/notification-topic/notification-topic";
import NotificationTopicSchema from "notifications/values/notification-topic/notification-topic-schema";
import RecipientName from "notifications/values/recipient-name/recipient-name";
import SenderName from "notifications/values/sender-name/sender-name";
import { z } from "zod";

export type NotificationJSON = z.infer<typeof NotificationSchema.NotificationJSONZ>;
export type NotificationParams = z.infer<typeof NotificationSchema.NotificationZ>;

export default class NotificationSchema {
  public static readonly NotificationJSONZ = z.object({
    id: z.uuid(),
    recipients: z.array(NotificationRecipientSchema.NotificationRecipientJSONZ),
    senderId: z.uuid().nullish(),
    message: z.string().nullish(),
    actionRequired: z.enum(ActionEnum).nullish(),
    createdDate: z.iso.datetime({ offset: true }),
    isCanceled: z.boolean().nullish(),
    senderName: z.string().nullish(),
    recipientName: z.string().nullish(),
    topicCategory: z.string().nullish(),
    topicId: z.uuid().nullish(),
  });

  public static readonly NotificationZ = z.object({
    id: z.instanceof(Guid),
    recipients: z.array(z.instanceof(NotificationRecipient)),
    senderUserId: z.instanceof(Guid).optional(),
    message: z.instanceof(NotificationMessage).optional(),
    topic: z.instanceof(NotificationTopic),
    actionRequired: z.enum(ActionEnum).optional(),
    createdDate: z.instanceof(AHDate),
    isCanceled: z.boolean().optional(),
    senderName: z.instanceof(SenderName).optional(),
    recipientName: z.instanceof(RecipientName).optional(),
  });

  public static validate(values: unknown): NotificationParams {
    return parseOrThrow(NotificationSchema.NotificationZ, values, {
      logError: true,
      schemaName: "NotificationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): NotificationJSON {
    return parseOrThrow(NotificationSchema.NotificationJSONZ, values, {
      logError: true,
      schemaName: "NotificationSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is NotificationParams {
    try {
      NotificationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is NotificationJSON {
    try {
      NotificationSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: unknown): NotificationParams {
    const parsed = parseOrThrow(NotificationSchema.NotificationJSONZ, data, {
      logError: true,
      schemaName: "NotificationSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return NotificationSchema.codec.decode(parsed);
  }

  public static serialize(notification: NotificationParams | null | undefined): NotificationJSON | null | undefined {
    if (isNullOrUndefined(notification)) return notification;
    const parsed = NotificationSchema.validate(notification);
    if (isNullOrUndefined(parsed)) return parsed;
    return NotificationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(NotificationSchema.NotificationJSONZ, NotificationSchema.NotificationZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id)!,
      recipients: data.recipients.map((r) => new NotificationRecipient(NotificationRecipientSchema.deserialize(r))),
      senderUserId: Guid.fromJSON(data.senderId) ?? undefined,
      message: NotificationMessage.fromJSON(data.message) ?? undefined,
      topic: new NotificationTopic(
        NotificationTopicSchema.deserialize({ topicId: data.topicId, topicCategory: data.topicCategory })
      ),
      actionRequired: data.actionRequired ?? undefined,
      createdDate: new AHDate(DateSchema.deserialize(data.createdDate)),
      isCanceled: data.isCanceled ?? undefined,
      senderName: SenderName.fromJSON(data.senderName) ?? undefined,
      recipientName: RecipientName.fromJSON(data.recipientName) ?? undefined,
    }),
    encode: (notification) => ({
      id: notification.id.value,
      recipients: notification.recipients.map((r) => ({
        userId: r.userId.value,
        seenAt: r.seenAt?.toISOString() ?? null,
      })),
      senderId: notification.senderUserId?.toJSON(),
      message: notification.message?.value,
      topicId: notification.topic?.id?.toJSON(),
      topicCategory: notification.topic?.category,
      actionRequired: notification.actionRequired ?? undefined,
      createdDate: notification.createdDate?.toISOString() ?? "",
      isCanceled: notification.isCanceled,
      senderName: notification.senderName?.toJSON(),
      recipientName: notification.recipientName?.toJSON(),
    }),
  });
}
